import {useNavigate} from 'react-router-dom';
import {
    Alert,
    SimpleGrid,
    Stack,
    Text,
} from '@mantine/core';
import {AiOutlineWarning} from 'react-icons/ai';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {useTestRuns} from '../hooks/useTestRuns.ts';
import {useTestRunsSearch} from '../hooks/useTestRunsSearch.ts';
import {TestRunStatusLegend} from '../profile/TestRunStatusLegend.tsx';
import {TestRun} from '../types.ts';
import config from '../config';

// READ-ONLY (FNP-969). There is no selection here and no batch action: selection
// exists to feed a delete/archive affordance, and this module has neither —
// TestRun writes are @aws_iam. Do not add checkboxes without a write to justify
// them.
export const RecordsTable = () => {

    const navigate = useNavigate();

    const {
        // The full loaded set BEFORE keyword filtering — the window the footer's
        // "N of M loaded" claim is measured against.
        items,
        records,
        sortStatus,
        setSortStatus,
        columns,
        loading,
        isError,
    } = useTestRuns();

    // Same module-scoped store the list hook filters on (FNP-815): this component
    // owns the input, useTestRuns owns the filtering, and they must share one
    // keyword. Never swap this for a local useState.
    const {
        keyword,
        handleFilterByKeyword,
    } = useTestRunsSearch();

    // A failed load is STATED rather than rendered as an empty table — an empty
    // table and a broken query look identical otherwise, and here they mean
    // opposite things ("nothing has run yet" vs "we cannot tell you what ran").
    if (isError) {
        return (
            <Alert
                icon={<AiOutlineWarning/>}
                color='red'
                title='Could not load test runs'
            >
                The Testing API did not respond. Use Reload to try again; if it persists the
                API may be unavailable in this environment.
            </Alert>
        );
    }

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TestRunStatusLegend/>

            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            <MantineDataTable
                columns={columns}
                records={records}
                fetching={loading}
                // Sorting is WIRED, not merely declared on the columns: a
                // `sortable: true` column with no sortStatus/onSortStatusChange
                // renders the affordance and does nothing when clicked.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                withTableBorder={true}
                onRowClick={({record}) =>
                    navigate(`${config.basePath}/${encodeURIComponent((record as TestRun).id)}`)}
                // An empty runs table is NOT an error — it means no wake has yet
                // executed a definition. Said in words so nobody reads a blank
                // table as a broken one.
                noRecordsText='No test runs yet — no canary wake has executed a test definition in this environment.'
                emptyState={
                    <Stack align={'center'} gap={'xs'} p={'xl'}>
                        <Text fw={500}>No test runs yet</Text>
                        <Text size={'sm'} c={'dimmed'} ta={'center'} maw={460}>
                            A run appears here once a canary wake executes a test definition.
                            This is not an error — it means nothing has run in this environment
                            yet.
                        </Text>
                    </Stack>
                }
            />

            {/*
              The service drains nextToken before returning, so every run matching
              the filter is already loaded and there is never a further page to
              request — nextToken is null by construction, not by omission.
            */}
            <DataTableTokenPagination
                loadedCount={records.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'test run'}
                nextToken={null}
                handleLoadMore={() => {
                }}
            />
        </SimpleGrid>
    );
};
